import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
// eslint-disable-next-line no-restricted-imports -- follow-up: use useCurrentLocalization()
import { TFunction } from "i18next";
import {
  AppError,
  Avatar,
  Button,
  EmptyState,
  Loader,
  Search,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  Tag,
  toast,
} from "@bfc-software/pallet";
import { logger } from "@bfc-software/app-toolbox";
import {
  useTranslation,
  TRANSLATION_NAMESPACE,
} from "@bfc-software/bfc-web-ui/i18n";
import { apiClient } from "@bfc-software/bfc-web-ui/lib/aaa-api-client";
import { DateValue } from "@bfc-software/bfc-web-ui/ui/shared/date-value";

import { useManageTenant } from "../../-data/use-manage-tenant";
import { DriverImportModal } from "../-components/driver-import-modal";
import { DriverQrCodesModal } from "../-components/driver-qr-modal";
import {
  AddDriverModal,
  EditNameModal,
  ResetPinModal,
  type Driver,
} from "../-components/driver-modals";

/**
 * The AAA `app_name` Deliver's drivers sign in under. App users are shared
 * across every app on a warehouse; this is what scopes the list to drivers.
 */
const APP_NAME = "deliver";

export const Route = createFileRoute(
  "/$lang/_layout/orgs/$org/_layout/deliver/manage/$tenant/drivers",
)({
  component: RouteComponent,
});

/** A driver created before names existed, or without one, shows the login id. */
const displayNameOf = (driver: Driver) => driver.user_name || driver.user_id;

/**
 * openapi-react-query throws the parsed apiResponse body itself
 * (`{ code, message }`) rather than an `Error`.
 */
function errorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === "object") {
    const body = error as { message?: unknown; error?: unknown };
    if (typeof body.message === "string" && body.message) return body.message;
    if (typeof body.error === "string" && body.error) return body.error;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

function DriverRow({
  driver,
  isTogglePending,
  onEditName,
  onResetPin,
  onToggle,
  t,
}: {
  driver: Driver;
  isTogglePending: boolean;
  onEditName: () => void;
  onResetPin: () => void;
  onToggle: () => void;
  t: TFunction;
}) {
  return (
    <TableRow data-test-role="driver-row">
      <TableCell>
        <div className="flex items-center gap-12">
          <Avatar name={displayNameOf(driver)} colorKey={driver.user_id} />
          <span className="font-strong text-on-surface">
            {displayNameOf(driver)}
          </span>
        </div>
      </TableCell>
      <TableCell>
        <span className="font-mono font-strong text-on-surface">
          {driver.user_id}
        </span>
      </TableCell>
      <TableCell>
        <Tag
          label={
            driver.active
              ? t("manage.drivers.status.active")
              : t("manage.drivers.status.inactive")
          }
          display={{ tone: driver.active ? "success" : "neutral" }}
        />
      </TableCell>
      <TableCell>
        <DateValue dateValue={driver.created_at} />
      </TableCell>
      <TableCell>
        <div className="flex justify-end gap-8">
          <Button
            kind="icon-only"
            variant="tertiary"
            icon="Edit2"
            label={t("manage.drivers.rowActions.editName")}
            onPress={onEditName}
            data-test-role="driver-edit-name"
          />
          <Button
            kind="text-only"
            variant="secondary"
            scale="compact"
            text={t("manage.drivers.rowActions.resetPin")}
            onPress={onResetPin}
            data-test-role="driver-reset-pin"
          />
          <Button
            kind="text-only"
            variant={driver.active ? "danger-container" : "success-container"}
            scale="compact"
            text={
              driver.active
                ? t("manage.drivers.rowActions.deactivate")
                : t("manage.drivers.rowActions.activate")
            }
            isDisabled={isTogglePending}
            onPress={onToggle}
            data-test-role="driver-toggle-status"
          />
        </div>
      </TableCell>
    </TableRow>
  );
}

/**
 * Manage Warehouse > Drivers: the AAA accounts (`app_name: "deliver"`) drivers
 * sign in to the Deliver app with. The same `/app_user/*` calls as the Outbound
 * Selectors screen, scoped to the tenant's own org and warehouse.
 *
 * Import creates accounts for Dakota's active drivers (the config service's
 * `/orgs/deliver/drivers/import*`); Driver QR Codes prints their sign-in codes.
 */
function RouteComponent() {
  const params = Route.useParams();
  const { tenant } = useManageTenant();
  const { t } = useTranslation({
    lang: params.lang,
    namespace: TRANSLATION_NAMESPACE.deliver,
  });

  const client = useMemo(() => apiClient(), []);

  // The shell renders this only once the tenant has resolved.
  const orgId = tenant?.orgId ?? "";
  const warehouseId = tenant?.warehouseCode ?? "";
  const scope = {
    app_name: APP_NAME,
    org_id: orgId,
    warehouse_id: warehouseId,
  };

  const listQuery = client.useQuery(
    "get",
    "/app_user/list",
    { params: { query: scope } },
    { enabled: Boolean(tenant) },
  );
  const createMutation = client.useMutation("post", "/app_user/create");
  const updateMutation = client.useMutation("post", "/app_user/update");
  const resetPinMutation = client.useMutation("post", "/app_user/reset_pin");

  const [q, setQ] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState<Driver | null>(null);
  const [nameTarget, setNameTarget] = useState<Driver | null>(null);
  const [statusPendingId, setStatusPendingId] = useState<number | null>(null);

  const drivers = (listQuery.data ?? []) as Driver[];
  const needle = q.trim().toLowerCase();
  const visible = drivers
    .filter(
      (d) =>
        !needle ||
        d.user_id.toLowerCase().includes(needle) ||
        (d.user_name ?? "").toLowerCase().includes(needle),
    )
    .sort((a, b) => a.user_id.localeCompare(b.user_id));

  const handleCreate = async (userId: string, pin: string, name: string) => {
    try {
      await createMutation.mutateAsync({
        body: {
          ...scope,
          user_id: userId,
          pin,
          ...(name ? { user_name: name } : {}),
        },
      });
      logger.info("Created driver", { userId });
      listQuery.refetch();
    } catch (error) {
      logger.warn("Failed to create driver", { error, userId });
      toast.error(errorMessage(error, t("manage.drivers.add.failedFallback")));
      throw error;
    }
  };

  const handleResetPin = async (pin: string) => {
    if (!resetTarget) return;
    const userId = resetTarget.user_id;
    try {
      await resetPinMutation.mutateAsync({
        body: { ...scope, user_id: userId, new_pin: pin },
      });
      logger.info("Reset driver PIN", { userId });
    } catch (error) {
      logger.warn("Failed to reset driver PIN", { error, userId });
      toast.error(
        errorMessage(error, t("manage.drivers.resetPin.failedFallback")),
      );
      throw error;
    }
  };

  const handleEditName = async (name: string) => {
    if (!nameTarget) return;
    const userId = nameTarget.user_id;
    try {
      await updateMutation.mutateAsync({
        body: { ...scope, user_id: userId, user_name: name },
      });
      logger.info("Renamed driver", { userId });
      toast.success(t("manage.drivers.toast.renamed", { userId }));
      listQuery.refetch();
    } catch (error) {
      logger.warn("Failed to rename driver", { error, userId });
      toast.error(
        errorMessage(error, t("manage.drivers.editName.failedFallback")),
      );
      throw error;
    }
  };

  const handleToggleStatus = (driver: Driver) => {
    if (updateMutation.isPending) return;
    const nextActive = !driver.active;

    setStatusPendingId(driver.id);
    updateMutation
      .mutateAsync({
        body: { ...scope, user_id: driver.user_id, active: nextActive },
      })
      .then(() => {
        logger.info("Updated driver status", {
          userId: driver.user_id,
          active: nextActive,
        });
        toast.success(
          t(
            nextActive
              ? "manage.drivers.toast.activated"
              : "manage.drivers.toast.deactivated",
            { userId: driver.user_id },
          ),
        );
        listQuery.refetch();
      })
      .catch((error: unknown) => {
        logger.warn("Failed to update driver status", {
          error,
          userId: driver.user_id,
        });
        toast.error(
          errorMessage(
            error,
            t("manage.drivers.toast.statusFailed", { userId: driver.user_id }),
          ),
        );
      })
      .finally(() => setStatusPendingId(null));
  };

  return (
    <div className="bg-surface h-full w-full">
      <div className="page-shell">
        <header className="page-header">
          <div className="page-header-left">
            <h1 className="page-title">{t("manage.drivers.heading")}</h1>
            <p className="page-subtitle">
              {tenant?.name} — {tenant?.warehouseCode}
            </p>
          </div>
          <div className="page-header-right">
            <div className="page-header-searches">
              <Search
                aria-label={t("manage.drivers.searchAriaLabel")}
                placeholder={t("manage.drivers.searchPlaceholder")}
                value={q}
                onChange={(value) => setQ(String(value))}
                onClear={() => setQ("")}
              />
            </div>
            <div className="page-header-actions">
              <Button
                kind="text-icon-left"
                variant="secondary"
                icon="Download"
                text={t("manage.drivers.import.button")}
                isDisabled={!tenant}
                onPress={() => setImportOpen(true)}
                data-test-role="import-drivers"
              />
              <Button
                kind="text-icon-left"
                variant="secondary"
                icon="Grid"
                text={t("manage.drivers.qr.button")}
                isDisabled={!tenant}
                onPress={() => setQrOpen(true)}
                data-test-role="driver-qr-codes"
              />
              <Button
                kind="text-icon-left"
                variant="avatar-surface"
                icon="Plus"
                text={t("manage.drivers.addButton")}
                onPress={() => setCreateOpen(true)}
              />
            </div>
          </div>
        </header>

        <div className="table-frame">
          {listQuery.isLoading ? (
            <Loader type="page" />
          ) : listQuery.isError ? (
            <AppError
              description={t("manage.drivers.failed")}
              resolution={t("manage.failureSupport")}
            />
          ) : (
            <Table
              aria-label={t("manage.drivers.table.ariaLabel")}
              isResizable={false}
            >
              <TableHead>
                <TableHeaderCell isRowHeader={true}>
                  {t("manage.drivers.table.driver")}
                </TableHeaderCell>
                <TableHeaderCell>
                  {t("manage.drivers.table.userId")}
                </TableHeaderCell>
                <TableHeaderCell>
                  {t("manage.drivers.table.status")}
                </TableHeaderCell>
                <TableHeaderCell>
                  {t("manage.drivers.table.created")}
                </TableHeaderCell>
                <TableHeaderCell>
                  {t("manage.drivers.table.actions")}
                </TableHeaderCell>
              </TableHead>
              <TableBody>
                {visible.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <EmptyState heading={t("manage.drivers.empty")} />
                    </TableCell>
                  </TableRow>
                ) : (
                  visible.map((driver) => (
                    <DriverRow
                      key={driver.id}
                      driver={driver}
                      isTogglePending={statusPendingId === driver.id}
                      onEditName={() => setNameTarget(driver)}
                      onResetPin={() => setResetTarget(driver)}
                      onToggle={() => handleToggleStatus(driver)}
                      t={t}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      <AddDriverModal
        isOpen={createOpen}
        onOpenChange={setCreateOpen}
        onCreate={handleCreate}
        isPending={createMutation.isPending}
        t={t}
      />

      <DriverImportModal
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => listQuery.refetch()}
        orgId={orgId}
        warehouseId={warehouseId}
        t={t}
      />

      <DriverQrCodesModal
        isOpen={qrOpen}
        onClose={() => setQrOpen(false)}
        orgId={orgId}
        warehouseId={warehouseId}
        t={t}
      />

      <ResetPinModal
        driver={resetTarget}
        onOpenChange={(open) => {
          if (!open) setResetTarget(null);
        }}
        onSubmit={handleResetPin}
        isPending={resetPinMutation.isPending}
        t={t}
      />

      <EditNameModal
        driver={nameTarget}
        onOpenChange={(open) => {
          if (!open) setNameTarget(null);
        }}
        onSubmit={handleEditName}
        isPending={updateMutation.isPending && statusPendingId === null}
        t={t}
      />
    </div>
  );
}
